import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { apiClient, formatDate, POLE_LABELS, TICKET_STATUS_LABELS, TICKET_STATUS_META, TICKET_PRIORITY_LABELS, TICKET_PRIORITY_META, TICKET_CATEGORY_LABELS } from "@/lib/api";
import { usePoles } from "@/context/PolesContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import BulkConfirmDialog from "@/components/BulkConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { MagnifyingGlass, Ticket as TicketIcon, ArrowRight, Lightning, CheckCircle, Copy, Archive, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = Object.keys(TICKET_STATUS_LABELS);
const PRIORITIES = Object.keys(TICKET_PRIORITY_LABELS);

const COMPTA_TEMPLATES = [
  {
    id: "rib_remboursement",
    label: "Remboursement — demande de RIB",
    text: "Bonjour,\nAfin de procéder au remboursement de votre commande, merci de nous transmettre votre RIB en réponse à ce message.\nDès réception, notre service comptabilité pourra effectuer le remboursement dans les meilleurs délais.\nCordialement,",
  },
];

export default function TicketsList() {
  const { poles, poleLabel } = usePoles();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quickOpen, setQuickOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    reference: "",
    transaction_number: "",
    client_email: "",
    service_id: "all",
    status: "all",
    priority: "all",
  });

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/tickets/stats");
      setStats(data);
    } catch (err) {
      console.warn("fetchStats failed", err);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all") params[k] = v;
      });
      if (showArchived) params.archived = true;
      const { data } = await apiClient.get("/tickets", { params });
      setItems(data);
      setSelected(new Set());
    } catch (err) {
      console.error("fetchList failed", err);
      toast.error(err.response?.data?.detail || "Erreur de chargement des tickets");
    } finally {
      setLoading(false);
    }
  }, [filters, showArchived]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const id = setTimeout(fetchList, 250);
    return () => clearTimeout(id);
  }, [fetchList]);

  const canQuick = user && (user.role === "admin" || user.role === "responsable");

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = items.length > 0 && items.every((t) => selected.has(t.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((t) => t.id)));
  const runBulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await apiClient.post("/tickets/bulk-action", { action, ticket_ids: ids });
      const verb = action === "close" ? "clôturé(s)" : "archivé(s)";
      toast.success(`${data.done} ticket(s) ${verb}${data.skipped ? ` · ${data.skipped} ignoré(s)` : ""}`);
      setSelected(new Set());
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'action groupée");
    } finally {
      setConfirmAction(null);
    }
  };

  return (
    <>
      <Topbar
        title="Tickets clients"
        subtitle="Gestion centralisée des demandes clients"
        actions={canQuick ? (
          <Button
            onClick={() => setQuickOpen(true)}
            variant="outline"
            className="rounded-sm h-9 px-3 sm:px-4 gap-2 text-xs sm:text-sm border-zinc-300"
            data-testid="open-quick-ticket"
          >
            <Lightning size={16} weight="fill" className="text-amber-500" />
            <span className="hidden sm:inline">Ticket rapide</span>
            <span className="sm:hidden">Rapide</span>
          </Button>
        ) : null}
      />

      {canQuick && (
        <QuickTicketDialog
          open={quickOpen}
          onOpenChange={setQuickOpen}
          onCreated={() => { fetchList(); fetchStats(); }}
          navigate={navigate}
        />
      )}

      <BulkConfirmDialog action={confirmAction} count={selected.size} onCancel={() => setConfirmAction(null)} onConfirm={() => runBulk(confirmAction)} />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="tickets-list">
        {/* Status counters */}
        <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-0 border border-zinc-200 bg-white mb-5" data-testid="tickets-stats">
          {STATUSES.map((st, i) => (
            <button
              key={st}
              onClick={() => setF("status", st === filters.status ? "all" : st)}
              className={`px-4 py-4 text-left ${i > 0 ? "border-l border-zinc-200" : ""} hover:bg-zinc-50 ${filters.status === st ? "bg-zinc-100" : ""}`}
              data-testid={`filter-status-${st}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`w-2 h-2 rounded-full ${TICKET_STATUS_META[st].dot}`} />
                <span className="label-tiny text-zinc-500">{TICKET_STATUS_LABELS[st]}</span>
              </div>
              <div className="font-heading text-2xl font-semibold tabular-nums">{stats.by_status?.[st] || 0}</div>
            </button>
          ))}
        </section>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-4 sm:p-5 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input placeholder="Recherche libre..." value={filters.q} onChange={(e) => setF("q", e.target.value)} className="h-10 pl-9 rounded-sm border-zinc-300" data-testid="filter-q" />
            </div>
            <Input placeholder="Référence ECO-..." value={filters.reference} onChange={(e) => setF("reference", e.target.value)} className="h-10 rounded-sm border-zinc-300 font-mono" data-testid="filter-reference" />
            <Input placeholder="N° transaction" value={filters.transaction_number} onChange={(e) => setF("transaction_number", e.target.value)} className="h-10 rounded-sm border-zinc-300 font-mono" data-testid="filter-transaction" />
            <Input placeholder="E-mail client" value={filters.client_email} onChange={(e) => setF("client_email", e.target.value)} className="h-10 rounded-sm border-zinc-300" data-testid="filter-email" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={filters.service_id} onValueChange={(v) => setF("service_id", v)}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="filter-service"><SelectValue placeholder="Service" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous services</SelectItem>
                  {poles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.priority} onValueChange={(v) => setF("priority", v)}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="filter-priority"><SelectValue placeholder="Priorité" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {PRIORITIES.map((p) => (<SelectItem key={p} value={p}>{TICKET_PRIORITY_LABELS[p]}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white border border-zinc-200">
          <div className="px-4 sm:px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="label-tiny text-zinc-500">{showArchived ? "Archives" : "Résultats"}</span>
              <button
                onClick={() => setShowArchived((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium border rounded-sm transition-colors ${showArchived ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}
                data-testid="toggle-archived"
              >
                <Archive size={13} weight="bold" />
                {showArchived ? "Voir les actifs" : "Voir les archives"}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-zinc-600" data-testid="tickets-count">{items.length} ticket{items.length > 1 ? "s" : ""}</span>
            </div>
          </div>
          {selected.size > 0 && (
            <div className="px-4 sm:px-5 py-2.5 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-action-bar">
              <span className="text-xs font-medium">{selected.size} sélectionné{selected.size > 1 ? "s" : ""}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setConfirmAction("close")} className="rounded-sm h-8 text-xs bg-white text-zinc-900 hover:bg-zinc-200 gap-1.5" data-testid="bulk-close-btn">
                  <CheckCircle size={14} weight="bold" /> Clôturer
                </Button>
                <Button size="sm" onClick={() => setConfirmAction("archive")} className="rounded-sm h-8 text-xs bg-white text-zinc-900 hover:bg-zinc-200 gap-1.5" data-testid="bulk-archive-btn">
                  <Archive size={14} weight="bold" /> Archiver
                </Button>
                <button onClick={() => setSelected(new Set())} className="text-xs text-zinc-300 hover:text-white px-2" data-testid="bulk-clear-btn">Annuler</button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-zinc-500">Chargement...</div>
          ) : !items.length ? (
            <div className="px-6 py-16 text-center">
              <TicketIcon size={30} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
              <div className="text-sm text-zinc-500">Aucun ticket</div>
            </div>
          ) : (
            <>
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="px-4 py-3 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" aria-label="Tout sélectionner" /></th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Référence</th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Client</th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Sujet</th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Service</th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Statut</th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Priorité</th>
                      <th className="text-left px-4 py-3 label-tiny text-zinc-500">Dernière activité</th>
                      <th className="px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t) => (
                      <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${selected.has(t.id) ? "bg-zinc-50" : ""}`} data-testid={`ticket-row-${t.id}`}>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggleOne(t.id)} data-testid={`bulk-select-${t.id}`} aria-label="Sélectionner le ticket" /></td>
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-zinc-900">{t.reference}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{t.client_first_name} {t.client_last_name}</div>
                          <div className="text-xs text-zinc-500 truncate max-w-[180px]">{t.client_email}</div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <div className="font-medium truncate">{t.subject}</div>
                          <div className="text-[11px] text-zinc-500">{TICKET_CATEGORY_LABELS[t.category] || t.category}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">{t.service_id ? poleLabel(t.service_id) : <span className="text-zinc-400">—</span>}</td>
                        <td className="px-4 py-3"><div className="flex items-center gap-1.5 flex-wrap"><StatusChip status={t.status} />{t.is_overdue && <OverdueBadge />}</div></td>
                        <td className={`px-4 py-3 text-xs ${TICKET_PRIORITY_META[t.priority]}`}>{TICKET_PRIORITY_LABELS[t.priority]}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-zinc-500 whitespace-nowrap">{formatDate(t.last_activity_at || t.created_at)}</td>
                        <td className="px-4 py-3 text-right"><ArrowRight size={14} weight="bold" className="text-zinc-400" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="md:hidden divide-y divide-zinc-100">
                {items.map((t) => (
                  <div key={t.id} className={`flex items-start gap-3 px-4 py-3 ${selected.has(t.id) ? "bg-zinc-50" : ""}`}>
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggleOne(t.id)} data-testid={`bulk-select-${t.id}`} aria-label="Sélectionner le ticket" />
                    </div>
                    <button onClick={() => navigate(`/tickets/${t.id}`)} className="flex-1 text-left min-w-0" data-testid={`ticket-card-${t.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-mono text-xs font-semibold">{t.reference}</span>
                        <div className="flex items-center gap-1.5">{t.is_overdue && <OverdueBadge />}<StatusChip status={t.status} /></div>
                      </div>
                      <div className="font-medium text-sm truncate">{t.subject}</div>
                      <div className="text-xs text-zinc-500 truncate mt-0.5">{t.client_first_name} {t.client_last_name} · {t.client_email}</div>
                      <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                        {t.service_id && <span className="px-1.5 py-0.5 bg-zinc-100">{poleLabel(t.service_id)}</span>}
                        <span className={TICKET_PRIORITY_META[t.priority]}>{TICKET_PRIORITY_LABELS[t.priority]}</span>
                        <span className="font-mono">{formatDate(t.last_activity_at || t.created_at)}</span>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}

function StatusChip({ status }) {
  const m = TICKET_STATUS_META[status] || TICKET_STATUS_META.new;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {TICKET_STATUS_LABELS[status] || status}
    </span>
  );
}

function OverdueBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-red-600 text-white" data-testid="overdue-badge" title="Sans réponse depuis plus de 72h — remonté automatiquement">
      <WarningCircle size={11} weight="fill" /> En retard
    </span>
  );
}

const EMPTY_FORM = {
  client_email: "",
  client_first_name: "",
  client_last_name: "",
  client_phone: "",
  transaction_number: "",
  service_id: "none",
  subject: "",
  message: "",
};

function QuickTicketDialog({ open, onOpenChange, onCreated, navigate }) {
  const { poles } = usePoles();
  const [form, setForm] = useState(EMPTY_FORM);
  const [template, setTemplate] = useState("custom");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) { setForm(EMPTY_FORM); setTemplate("custom"); setSuccess(null); }
  }, [open]);

  const isCompta = form.service_id === "comptabilite";

  const applyTemplate = (id) => {
    setTemplate(id);
    if (id === "custom") return;
    const t = COMPTA_TEMPLATES.find((x) => x.id === id);
    if (t) {
      setForm((s) => ({ ...s, message: t.text, subject: s.subject || t.label }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const email = form.client_email.trim();
    if (!email || !email.includes("@")) { toast.error("E-mail client obligatoire et valide"); return; }
    if (!form.message.trim()) { toast.error("Le message au client est obligatoire"); return; }
    if (!form.transaction_number.trim()) { toast.error("Le numéro de transaction est obligatoire"); return; }
    setSubmitting(true);
    try {
      const payload = {
        client_email: email,
        client_first_name: form.client_first_name.trim() || null,
        client_last_name: form.client_last_name.trim() || null,
        client_phone: form.client_phone.trim() || null,
        transaction_number: form.transaction_number.trim(),
        service_id: form.service_id === "none" ? null : form.service_id,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      };
      const { data } = await apiClient.post("/tickets/quick", payload);
      const trackingUrl = `${window.location.origin}/portail/suivi/${data.tracking_token}`;
      setSuccess({ ...data, trackingUrl });
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-md max-h-[90vh] overflow-y-auto" data-testid="quick-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Ticket rapide</DialogTitle>
          <DialogDescription>
            Ouvrez un ticket et envoyez un message au client. Seul l'e-mail est obligatoire.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-4" data-testid="quick-ticket-success">
            <div className="flex items-center gap-3">
              <CheckCircle size={28} weight="fill" className="text-green-600" />
              <div>
                <div className="font-semibold">Ticket créé & message envoyé</div>
                <div className="text-xs text-zinc-500">Un e-mail avec le lien de suivi a été envoyé au client.</div>
              </div>
            </div>
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              <div className="px-3 py-2">
                <div className="label-tiny text-zinc-500">Référence</div>
                <div className="font-mono font-semibold" data-testid="quick-success-reference">{success.reference}</div>
              </div>
              <div className="px-3 py-2">
                <div className="label-tiny text-zinc-500">Lien de suivi</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-[11px] bg-zinc-50 border border-zinc-200 px-2 py-1.5 break-all">{success.trackingUrl}</code>
                  <button onClick={() => { navigator.clipboard.writeText(success.trackingUrl); toast.success("Copié"); }} className="p-2 border border-zinc-300 hover:bg-zinc-100" title="Copier">
                    <Copy size={14} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="rounded-sm h-9 border-zinc-300" onClick={() => onOpenChange(false)}>Fermer</Button>
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9" onClick={() => navigate(`/tickets/${success.id}`)} data-testid="quick-goto-ticket">Ouvrir le ticket</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="quick-ticket-form">
            <div>
              <Label className="label-tiny text-zinc-500">E-mail client *</Label>
              <Input type="email" value={form.client_email} onChange={(e) => setF("client_email", e.target.value)} className="h-10 rounded-sm border-zinc-300 mt-1" placeholder="client@email.fr" data-testid="quick-email" />
            </div>
            <div>
              <Label className="label-tiny text-zinc-500">N° de transaction *</Label>
              <Input value={form.transaction_number} onChange={(e) => setF("transaction_number", e.target.value)} className="h-10 rounded-sm border-zinc-300 mt-1 font-mono" placeholder="Ex: TRX-2026-0001" data-testid="quick-transaction" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tiny text-zinc-500">Prénom</Label>
                <Input value={form.client_first_name} onChange={(e) => setF("client_first_name", e.target.value)} className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="quick-first" />
              </div>
              <div>
                <Label className="label-tiny text-zinc-500">Nom</Label>
                <Input value={form.client_last_name} onChange={(e) => setF("client_last_name", e.target.value)} className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="quick-last" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tiny text-zinc-500">Téléphone</Label>
                <Input value={form.client_phone} onChange={(e) => setF("client_phone", e.target.value)} className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="quick-phone" />
              </div>
              <div>
                <Label className="label-tiny text-zinc-500">Pôle concerné</Label>
                <Select value={form.service_id} onValueChange={(v) => setF("service_id", v)}>
                  <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="quick-service"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {poles.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-tiny text-zinc-500">Sujet</Label>
              <Input value={form.subject} onChange={(e) => setF("subject", e.target.value)} className="h-10 rounded-sm border-zinc-300 mt-1" placeholder="Ex: Remboursement" data-testid="quick-subject" />
            </div>
            {isCompta && (
              <div data-testid="quick-template-block">
                <Label className="label-tiny text-zinc-500">Modèle de message (comptabilité)</Label>
                <Select value={template} onValueChange={applyTemplate}>
                  <SelectTrigger className="h-10 rounded-sm border-zinc-300 mt-1" data-testid="quick-template"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPTA_TEMPLATES.map((t) => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}
                    <SelectItem value="custom">Écrire un message personnalisé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="label-tiny text-zinc-500">Message au client *</Label>
              <Textarea value={form.message} onChange={(e) => { setF("message", e.target.value); setTemplate("custom"); }} className="rounded-sm border-zinc-300 min-h-[120px] mt-1" placeholder="Rédigez le message envoyé au client à l'ouverture du ticket..." data-testid="quick-message" />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="outline" className="rounded-sm h-9 border-zinc-300" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9 gap-2" data-testid="quick-submit">
                <Lightning size={15} weight="fill" />
                {submitting ? "Création..." : "Créer & envoyer"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

